import React, { useEffect, useState, useMemo } from "react";
import NavigationButtons from "../../Components/adminDashboard/NavigationButtons";
import { getAllFilteredAppointments, setClinicProfile } from "../../Redux/AdminReducer/action";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import DashboardMetricTables from "../../Components/adminDashboard/DashboardMetricTables";
import DashboardGraphs from "../../Components/adminDashboard/DashboardGraphs";
import { StatCard, DataTable, Button, Badge } from "../../Components/UI";
import {
  FaCalendarCheck,
  FaClinicMedical,
  FaVideo,
  FaUserMd,
  FaFilePrescription,
  FaPlus,
} from "react-icons/fa";
import OpenInteractionsDashboard from "./OpenInteractionsDashboard";

export default function AdminDashboard() {
  const [onlineAp, setOnlineAp] = useState(null);
  const [offlineAp, setOfflineAp] = useState(null);
  const [consultantAp, setConsultantAp] = useState(null);
  const [activeTab, setActiveTab] = useState("all"); // 'all' | 'offline' | 'online' | 'consultant'
  const [searchTerm, setSearchTerm] = useState("");

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const clinicProfile = useSelector((state) => state?.AdminReducer?.clinicProfile);
  const { onineAppointments, offlineAppointments, consultantAppointments } = useSelector(
    (state) => state?.AdminReducer || {}
  );

  const formatAppointments = (list, type) => {
    if (!Array.isArray(list)) return [];
    return list.map((a) => ({
      id: a?.patient?.id || a?._id,
      patientId: a?.patient?.id,
      appointmentId: a?._id,
      isOnline: a?.IsOnline === true,
      time: `${a?.bookTimeSlot?.startTime || '--:--'} - ${a?.bookTimeSlot?.endTime || '--:--'}`,
      name: a?.patient?.name || "Anonymous Patient",
      date: a?.bookDate ? format(new Date(a.bookDate), "dd MMM yyyy") : "N/A",
      type: type,
    }));
  };

  useEffect(() => {
    dispatch(setClinicProfile());
    dispatch(getAllFilteredAppointments({ IsOnline: true }, { pageSize: 30 }));
    dispatch(getAllFilteredAppointments({ IsOnline: false }, { pageSize: 30 }));
    dispatch(getAllFilteredAppointments({ IsConsultant: true }, { pageSize: 30 }));
  }, [dispatch]);

  useEffect(() => {
    document.title =
      typeof window !== "undefined" && window.location.hostname === "shivenclinic.com"
        ? "Clinic Management Software - One of India's Best EMR"
        : clinicProfile?.data?.ClinicName || "Clinic Management Software - One of India's Best EMR";
  }, [clinicProfile]);

  useEffect(() => {
    setOnlineAp(formatAppointments(onineAppointments, "Online"));
  }, [onineAppointments]);

  useEffect(() => {
    setOfflineAp(formatAppointments(offlineAppointments, "Offline"));
  }, [offlineAppointments]);

  useEffect(() => {
    if (consultantAppointments) {
      setConsultantAp(formatAppointments(consultantAppointments, "Consultant"));
    }
  }, [consultantAppointments]);

  // Combined and filtered appointments
  const allAppointments = useMemo(() => {
    const list = [
      ...(offlineAp || []),
      ...(onlineAp || []),
      ...(consultantAp || []),
    ];
    return list;
  }, [offlineAp, onlineAp, consultantAp]);

  const displayedAppointments = useMemo(() => {
    let source = allAppointments;
    if (activeTab === "offline") source = offlineAp || [];
    else if (activeTab === "online") source = onlineAp || [];
    else if (activeTab === "consultant") source = consultantAp || [];

    if (!searchTerm) return source;
    const term = searchTerm.toLowerCase();
    return source.filter(
      (item) =>
        item.name.toLowerCase().includes(term) ||
        item.time.toLowerCase().includes(term) ||
        item.date.toLowerCase().includes(term)
    );
  }, [activeTab, allAppointments, offlineAp, onlineAp, consultantAp, searchTerm]);

  const totalCount = allAppointments.length;
  const offlineCount = offlineAp?.length || 0;
  const onlineCount = onlineAp?.length || 0;
  const consultantCount = consultantAp?.length || 0;

  const appointmentHeaders = [
        { label: "Date", key: "date" },

    { label: "Patient Name", key: "name" },
    { label: "Time Slot", key: "time" },
    {
      label: "Type",
      key: "type",
      render: (val) => {
        const variants = {
          Online: "primary",
          Offline: "success",
          Consultant: "purple",
        };
        return <Badge variant={variants[val] || "default"}>{val}</Badge>;
      },
    },
    {
      label: "Action",
      key: "patientId",
      render: (patientId, row) => (
        <div className="flex items-center gap-2">
          {row?.isOnline && row?.appointmentId && (
            <Button
              size="xs"
              onClick={() => navigate(`/video-consultation/${row.appointmentId}`)}
              icon={<FaVideo className="w-3.5 h-3.5" />}
            >
              Join Call
            </Button>
          )}
          <Button
            size="xs"
            variant="outline"
            disabled={!patientId}
            onClick={() => patientId && navigate(`/prescription/add/${patientId}`)}
            icon={<FaFilePrescription className="w-3.5 h-3.5 text-sky-600" />}
          >
            Prescription
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="p-4 md:p-8 space-y-6 w-full">
      {/* Clinic Header & Quick Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm">
        <div className="flex items-center gap-3.5">
          {clinicProfile?.imageData ? (
            <img
              className="w-12 h-12 rounded-xl object-cover border border-slate-200"
              src={`data:image/jpeg;base64,${clinicProfile.imageData}`}
              alt="Clinic Logo"
            />
          ) : (
            <div className="w-12 h-12 rounded-xl bg-sky-500/10 text-sky-600 flex items-center justify-center font-bold text-xl">
              <FaClinicMedical className="w-6 h-6" />
            </div>
          )}

          <div>
            <h1 className="font-bold text-xl md:text-2xl text-slate-800 tracking-tight">
              {typeof window !== "undefined" && window.location.hostname === "shivenclinic.com"
                ? "Shiven Clinic Management"
                : clinicProfile?.data?.ClinicName || "Clinic Portal Dashboard"}
            </h1>
            <p className="text-xs text-slate-500 mt-0.5">
              Welcome back to your practice command center
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <NavigationButtons />
        </div>
      </div>

      {/* KPI Stat Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Appointments"
          value={totalCount}
          subtitle="Scheduled cases"
          icon={<FaCalendarCheck />}
          color="sky"
          onClick={() => setActiveTab("all")}
          className={activeTab === "all" ? "ring-2 ring-sky-500/40" : ""}
        />
        <StatCard
          title="Offline Visits"
          value={offlineCount}
          subtitle="In-clinic consultations"
          icon={<FaClinicMedical />}
          color="emerald"
          onClick={() => setActiveTab("offline")}
          className={activeTab === "offline" ? "ring-2 ring-emerald-500/40" : ""}
        />
        <StatCard
          title="Online Consultations"
          value={onlineCount}
          subtitle="Tele-health visits"
          icon={<FaVideo />}
          color="violet"
          onClick={() => setActiveTab("online")}
          className={activeTab === "online" ? "ring-2 ring-violet-500/40" : ""}
        />
        <StatCard
          title="Consultant Cases"
          value={consultantCount}
          subtitle="Specialist referrals"
          icon={<FaUserMd />}
          color="amber"
          onClick={() => setActiveTab("consultant")}
          className={activeTab === "consultant" ? "ring-2 ring-amber-500/40" : ""}
        />
      </div>

      {/* Pending Interactions & Leads Queue */}
      <OpenInteractionsDashboard />

      {/* Modern Appointments Section */}
      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
        {/* Tab Switcher Header */}
        <div className="p-4 border-b border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div className="flex items-center gap-2 overflow-x-auto">
            {[
              { key: "all", label: `All Appointments (${totalCount})` },
              { key: "offline", label: `Offline (${offlineCount})` },
              { key: "online", label: `Online (${onlineCount})` },
              { key: "consultant", label: `Consultant (${consultantCount})` },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-3.5 py-1.5 rounded-xl text-xs md:text-sm font-semibold transition whitespace-nowrap ${
                  activeTab === tab.key
                    ? "bg-sky-600 text-white shadow-sm shadow-sky-600/30"
                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="primary"
              onClick={() => navigate("/appointment")}
              icon={<FaPlus className="w-3.5 h-3.5" />}
            >
              New Appointment
            </Button>
          </div>
        </div>

        {/* Unified Appointments Table */}
        <DataTable
          headers={appointmentHeaders}
          data={displayedAppointments}
          onSearch={(term) => setSearchTerm(term)}
          emptyMessage="No appointments scheduled in this category."
        />
      </div>

      {/* Graphs ticked "Show on dashboard" on the Graphs page, data till date */}
      <DashboardGraphs />

      <DashboardMetricTables />

    </div>
  );
}
